document.addEventListener('DOMContentLoaded', function(){

// ========================================
// Utility functions for lazy video loading
// ========================================

function isDesktopPointerFine() {
  return window.matchMedia('(pointer: fine)').matches;
}

function prefersReducedData() {
  return window.matchMedia('(prefers-reduced-data: reduce)').matches;
}

function isYouTubeOrVimeo(url) {
  if (!url) return null;
  try {
    const urlObj = new URL(url, window.location.origin);
    const hostname = urlObj.hostname.toLowerCase();
    if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) {
      return 'youtube';
    }
    if (hostname.includes('vimeo.com')) {
      return 'vimeo';
    }
  } catch (e) {
    // Not a valid URL
  }
  return null;
}

function getYouTubeEmbedUrl(url, autoplay) {
  let videoId = '';
  try {
    const urlObj = new URL(url, window.location.origin);
    if (urlObj.hostname.includes('youtu.be')) {
      videoId = urlObj.pathname.slice(1);
    } else if (urlObj.hostname.includes('youtube.com')) {
      videoId = urlObj.searchParams.get('v');
    }
  } catch (e) {
    return null;
  }
  if (!videoId) return null;
  return `https://www.youtube.com/embed/${videoId}?autoplay=${autoplay ? 1 : 0}&mute=1&playsinline=1`;
}

function getVimeoEmbedUrl(url, autoplay) {
  let videoId = '';
  try {
    const urlObj = new URL(url, window.location.origin);
    const match = urlObj.pathname.match(/\/(\d+)/);
    if (match) videoId = match[1];
  } catch (e) {
    return null;
  }
  if (!videoId) return null;
  return `https://player.vimeo.com/video/${videoId}?autoplay=${autoplay ? 1 : 0}&muted=1&playsinline=1`;
}

function primeVideo(container) {
  if (container.dataset.primed === '1') return;
  
  const embedUrl = container.dataset.embedUrl;
  const embedType = isYouTubeOrVimeo(embedUrl);
  
  if (embedType) {
    // For YouTube/Vimeo, prepare iframe but don't inject yet
    let iframeSrc = '';
    if (embedType === 'youtube') {
      iframeSrc = getYouTubeEmbedUrl(embedUrl, false);
    } else if (embedType === 'vimeo') {
      iframeSrc = getVimeoEmbedUrl(embedUrl, false);
    }
    container.dataset.iframeSrc = iframeSrc;
    container.dataset.primed = '1';
    return;
  }
  
  // For regular video, attach sources and load
  const video = container.querySelector('[data-video-element]');
  if (!video) return;
  
  // Attach sources
  const sources = video.querySelectorAll('source[data-src]');
  sources.forEach(function(source) {
    source.src = source.dataset.src;
  });
  
  // If video has data-src, set it as src
  if (video.dataset.src) {
    video.src = video.dataset.src;
  }
  
  // Load the video (preload)
  video.load();
  container.dataset.primed = '1';
}

function activateVideo(container, options) {
  options = options || {};
  const autoplay = options.autoplay !== false;
  
  // If not primed yet, prime first
  if (container.dataset.primed !== '1') {
    primeVideo(container);
  }
  
  const embedUrl = container.dataset.embedUrl;
  const embedType = isYouTubeOrVimeo(embedUrl);
  
  if (embedType) {
    // Inject iframe with autoplay
    const poster = container.querySelector('[data-video-poster]');
    if (poster) poster.style.display = 'none';
    
    let iframeSrc = '';
    if (embedType === 'youtube') {
      iframeSrc = getYouTubeEmbedUrl(embedUrl, autoplay);
    } else if (embedType === 'vimeo') {
      iframeSrc = getVimeoEmbedUrl(embedUrl, autoplay);
    }
    
    const iframe = document.createElement('iframe');
    iframe.src = iframeSrc;
    iframe.className = 'media-video';
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';
    iframe.style.borderRadius = '8px';
    iframe.allow = 'autoplay; fullscreen; picture-in-picture';
    iframe.setAttribute('allowfullscreen', '');
    
    container.appendChild(iframe);
    container.dataset.activated = '1';
    return;
  }
  
  // For regular video
  const video = container.querySelector('[data-video-element]');
  const poster = container.querySelector('[data-video-poster]');
  const icon = container.querySelector('.video-play-icon');
  
  if (!video) return;
  
  // Show video, hide poster
  video.style.display = 'block';
  video.controls = true;
  if (poster) poster.style.display = 'none';
  
  // Play if autoplay
  if (autoplay) {
    video.play().catch(function(err) {
      console.log('Autoplay prevented:', err);
    });
  }
  
  container.dataset.activated = '1';
}

function attachVideoHandlers(container) {
  // Prevent double-binding
  if (container.dataset.handlersAttached === '1') return;
  container.dataset.handlersAttached = '1';
  
  const poster = container.querySelector('[data-video-poster]');
  const video = container.querySelector('[data-video-element]');
  const icon = container.querySelector('.video-play-icon');
  
  const isDesktop = isDesktopPointerFine();
  const reducedData = prefersReducedData();
  
  // Desktop: hover to prime (unless reduced-data), click to activate
  if (isDesktop && !reducedData) {
    if (poster) {
      poster.addEventListener('mouseenter', function() {
        primeVideo(container);
      });
    }
  }
  
  // Click/tap to activate
  if (poster) {
    poster.addEventListener('click', function() {
      const isMobile = !isDesktopPointerFine();
      activateVideo(container, { autoplay: true });
    });
    
    // Keyboard support
    poster.setAttribute('tabindex', '0');
    poster.setAttribute('role', 'button');
    poster.setAttribute('aria-label', 'Play video');
    poster.addEventListener('keydown', function(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        activateVideo(container, { autoplay: true });
      }
    });
  }
  
  // Video play/pause toggle (only for regular videos, not iframes)
  if (video) {
    video.addEventListener('click', function() {
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    });
    
    // Show/hide play icon
    video.addEventListener('play', function() {
      if (icon) icon.style.display = 'none';
    });
    video.addEventListener('pause', function() {
      if (icon) icon.style.display = '';
    });
  }
}

// ========================================
// Main initialization
// ========================================

if (typeof Flickity !== 'undefined') {
  const section = document.querySelector('.media-text-section');
  const mobileWrapper = document.createElement('div');
  mobileWrapper.className = 'mobile-slider';
  const mediaCols = section.querySelector('.media-grid-columns');
  if (mediaCols) {
    const items = [];
    ['.left-media', '.right-top', '.right-bottom'].forEach(function(sel){
      const el = mediaCols.querySelector(sel);
      if (el) {
        const clone = el.cloneNode(true);
        mobileWrapper.appendChild(clone);
      }
    });
    section.insertBefore(mobileWrapper, section.querySelector('.section-richtext'));
    mobileWrapper.style.display = 'none';
    window.addEventListener('resize', toggleSlider);
    toggleSlider();

    // Attach handlers to desktop video containers
    const videoContainers = mediaCols.querySelectorAll('[data-video-container]');
    videoContainers.forEach(attachVideoHandlers);
    
    function toggleSlider(){
      if (window.innerWidth <= 767) {
        mediaCols.style.display = 'none';
        mobileWrapper.style.display = 'block';
        if (!mobileWrapper.classList.contains('flickity-enabled')) {
          new Flickity(mobileWrapper, {
            cellAlign: 'center',
            contain: true,
            pageDots: false,
            prevNextButtons: false,
            selectedAttraction: 0.2,
            friction: 0.8,
            wrapAround: true
          });
          
          // Attach video handlers to mobile slider clones
          const mobileVideoContainers = mobileWrapper.querySelectorAll('[data-video-container]');
          mobileVideoContainers.forEach(attachVideoHandlers);
        }
      } else {
        mobileWrapper.style.display = 'none';
        mediaCols.style.display = 'grid';
      }
    }
  }
}
});

